// src/context/PropertyContext.jsx
// NOTE: This context is the single source of truth for:
// - Accessible property list (security-filtered)
// - Active property selection + persistence
// - Effective permissions for the active property
// - Portfolio (Total Property) permission intersection

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth, db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  getDocsFromCache,
  query,
  where,
  setDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  getPropertyAccessDocument,
  getPropertyDocument,
  getUserDocument,
} from "../utils/firestoreReadDeduper";

const PERMISSION_KEYS = [
  "inspections",
  "workOrders",
  "leasesCreate",
  "leasesView",
  "metrics",
  "expenses",
  "revenue",
  "rentCollection",
  "editProperty",
  "addProperty",
  "notices",
  "tasks",
];

const FULL_WRITE = Object.fromEntries(
  PERMISSION_KEYS.map((k) => [k, "readWrite"])
);
// Normalize permission values across versions:
// v2+: "none" | "read" | "write" (WRITE ONLY) | "readWrite"
// legacy (no permissionsVersion): "write" historically meant read+write.
function normalizePerm(raw, permissionsVersion) {
  const v = String(raw || "none");
  const ver = Number(permissionsVersion || 0);

  if (v === "none" || v === "read" || v === "write" || v === "readWrite") {
    if (ver < 2 && v === "write") return "readWrite"; // legacy semantics
    return v;
  }
  // legacy UI value
  if (v === "writeOnly") return "write";

  return "none";
}

function permHasRead(v) {
  return v === "read" || v === "readWrite";
}
function permHasWrite(v) {
  return v === "write" || v === "readWrite";
}
function intersectPerm(perms) {
  if (!Array.isArray(perms) || perms.length === 0) return "none";
  const allRead = perms.every((p) => permHasRead(p));
  const allWrite = perms.every((p) => permHasWrite(p));
  if (allRead && allWrite) return "readWrite";
  if (allRead) return "read";
  if (allWrite) return "write";
  return "none";
}

function resolvePermissionKey(key) {
  // Reports is a derived capability: metrics read implies reports read.
  if (String(key || "") === "reports") return "metrics";
  return key;
}


const PropertyContext = createContext(null);

export function PropertyProvider({ children }) {
  const [properties, setProperties] = useState([]);
  const [activeProperty, setActiveProperty] = useState(null);
  const [loadingProperties, setLoadingProperties] = useState(true);

  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserEmailLower, setCurrentUserEmailLower] = useState(null);
  const [currentUserIsGlobalAdmin, setCurrentUserIsGlobalAdmin] = useState(false);
  const [activePropertyDoc, setActivePropertyDoc] = useState(null);
  const [activePermissions, setActivePermissions] = useState(null);
  const [resolvedAccessPropertyId, setResolvedAccessPropertyId] = useState(null);

  // Map of propertyId -> { permissions, permissionsVersion }
  const [allPropertyPermissions, setAllPropertyPermissions] = useState({});
  const [resolvedPortfolioAccessKey, setResolvedPortfolioAccessKey] = useState(null);

  const [loadingAccess, setLoadingAccess] = useState(false);

  // ---------------------------------------------
  // Load saved active property from localStorage
  // ---------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem("activeProperty");
    setActiveProperty(saved || "all");
  }, []);

  // ---------------------------------------------
  // Persist active property
  // ---------------------------------------------
  useEffect(() => {
    if (activeProperty) {
      localStorage.setItem("activeProperty", activeProperty);
    }
  }, [activeProperty]);

  // ---------------------------------------------
  // Auth listener
  // ---------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setCurrentUser(u || null);
      setCurrentUserEmailLower(u?.email ? String(u.email).toLowerCase() : null);
      if (!u) {
        setCurrentUserIsGlobalAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  // ---------------------------------------------
// Load property list from Firestore (SECURE, RULE-SAFE)
// ---------------------------------------------
useEffect(() => {
  let cancelled = false;

  const fetchProperties = async () => {
    if (!currentUser || !currentUserEmailLower) {
      if (!cancelled) {
        setProperties([]);
        setCurrentUserIsGlobalAdmin(false);
        setLoadingProperties(false);
      }
      return;
    }

    setLoadingProperties(true);
    try {
      const emailLower = currentUserEmailLower;
      const results = new Map();
      const userSnap = await getUserDocument(db, currentUser.uid);
      const userData = userSnap.data() || {};
      const administratorStatus = await httpsCallable(functions, "getAdministratorStatus")({});
      const isGlobalAdmin = administratorStatus.data?.isGlobalAdmin === true;

      if (cancelled) return;
      setCurrentUserIsGlobalAdmin(isGlobalAdmin);

      // SUPERUSER: full access to all properties (unchanged)
      if (isGlobalAdmin) {
        const propertiesRef = collection(db, "properties");

        // A verified global administrator is authorized for the whole
        // collection. Show the SDK's previously authorized local snapshot
        // immediately, then replace it with the authoritative server result.
        // Other users never take this cache-first path.
        try {
          const cachedSnap = await getDocsFromCache(propertiesRef);
          if (!cancelled && !cachedSnap.empty) {
            cachedSnap.docs.forEach((d) =>
              results.set(d.id, { id: d.id, ...d.data() })
            );
            setProperties(Array.from(results.values()));
            setLoadingProperties(false);
          }
        } catch {
          // A cold browser has no cached snapshot; continue with the server.
        }

        const snap = await getDocs(propertiesRef);
        if (cancelled) return;
        results.clear();
        snap.docs.forEach((d) =>
          results.set(d.id, { id: d.id, ...d.data() })
        );
        setProperties(Array.from(results.values()));
        return;
      }

      // CANONICAL: load properties by explicit user-scoped propertyIds
      const propertyIds = Array.isArray(userData?.propertyIds)
        ? userData.propertyIds
        : [];

      const propertyDocs = await Promise.all(
        propertyIds.map(async (propertyId) => {
          try {
            const pSnap = await getPropertyDocument(db, propertyId);
            return pSnap.exists()
              ? { id: propertyId, ...pSnap.data() }
              : null;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      propertyDocs.forEach((property) => {
        if (property) results.set(property.id, property);
      });

      // LEGACY FALLBACKS (NON-DISCOVERY, RULE-SAFE)
      // If propertyIds is empty, allow owner/manager direct resolution
      if (!results.size) {
        const loadLegacyProperties = async (field) => {
          try {
            return await getDocs(
              query(
                collection(db, "properties"),
                where(field, "==", emailLower)
              )
            );
          } catch {
            return null;
          }
        };
        const [ownerSnap, managerSnap] = await Promise.all([
          loadLegacyProperties("owneremail"),
          loadLegacyProperties("manageremail"),
        ]);
        if (cancelled) return;
        [ownerSnap, managerSnap].forEach((snap) => {
          snap?.docs.forEach((d) =>
            results.set(d.id, { id: d.id, ...d.data() })
          );
        });
      }

      setProperties(Array.from(results.values()));
    } catch (err) {
      if (cancelled) return;
      console.error("Error loading properties", err);
      setCurrentUserIsGlobalAdmin(false);
      setProperties([]);
    } finally {
      if (!cancelled) setLoadingProperties(false);
    }
  };

  fetchProperties();
  return () => {
    cancelled = true;
  };
}, [currentUser, currentUserEmailLower]);

  

// ---------------------------------------------
// Load per-property access permissions for portfolio intersection (SECURE)
// ---------------------------------------------
const portfolioAccessKey = useMemo(
  () =>
    [
      currentUser?.uid || "",
      currentUserIsGlobalAdmin ? "admin" : "user",
      properties.map((p) => p.id).join("|"),
    ].join(":"),
  [currentUser, currentUserIsGlobalAdmin, properties]
);

useEffect(() => {
  let cancelled = false;

  const loadAll = async () => {
    // Single-property screens only need that property's access record. Defer
    // the portfolio-wide intersection until Total Portfolio is selected.
    if (activeProperty !== "all") {
      if (!cancelled) {
        setAllPropertyPermissions({});
        setResolvedPortfolioAccessKey(null);
      }
      return;
    }

    if (!currentUser || !currentUserEmailLower || !properties.length) {
      if (!cancelled) {
        setAllPropertyPermissions({});
        setResolvedPortfolioAccessKey(null);
      }
      return;
    }

    // Superusers and owners/managers get full read+write on each property.
    if (currentUserIsGlobalAdmin) {
      const full = {};
      properties.forEach((p) => {
        full[p.id] = { permissions: { ...FULL_WRITE }, permissionsVersion: 2 };
      });
      if (!cancelled) {
        setAllPropertyPermissions(full);
        setResolvedPortfolioAccessKey(portfolioAccessKey);
      }
      return;
    }

    setResolvedPortfolioAccessKey(null);
    const out = {};
    await Promise.all(
      properties.map(async (p) => {
        try {
          const owner =
            String(p.owneremail || "").toLowerCase() === currentUserEmailLower;
          const manager =
            String(p.manageremail || "").toLowerCase() === currentUserEmailLower;

          if (owner || manager) {
            out[p.id] = { permissions: { ...FULL_WRITE }, permissionsVersion: 2 };
            return;
          }

          const aSnap = await getPropertyAccessDocument(
            db,
            p.id,
            currentUser.uid
          );
          if (!aSnap.exists()) {
            // No explicit access doc -> treat as none (fails closed)
            out[p.id] = { permissions: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "none"])), permissionsVersion: 0 };
            return;
          }

          const data = aSnap.data() || {};
          const perms = data.permissions || {};
          const ver = data.permissionsVersion || 0;

          out[p.id] = {
            permissionsVersion: ver,
            permissions: Object.fromEntries(
              PERMISSION_KEYS.map((k) => [k, normalizePerm(perms[k], ver)])
            ),
          };
        } catch (e) {
          console.warn("[PropertyContext] Failed loading access for", p.id, e);
          out[p.id] = { permissions: Object.fromEntries(PERMISSION_KEYS.map((k) => [k, "none"])), permissionsVersion: 0 };
        }
      })
    );

    if (!cancelled) {
      setAllPropertyPermissions(out);
      setResolvedPortfolioAccessKey(portfolioAccessKey);
    }
  };

  loadAll();
  return () => {
    cancelled = true;
  };
}, [
  currentUser,
  currentUserEmailLower,
  currentUserIsGlobalAdmin,
  activeProperty,
  portfolioAccessKey,
  properties,
]);

// ---------------------------------------------
  // Load active property permissions (single-property mode)
  // ---------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const loadActive = async () => {
      if (
        !currentUser ||
        !currentUserEmailLower ||
        !activeProperty ||
        activeProperty === "all"
      ) {
        if (!cancelled) {
          setActivePropertyDoc(null);
          setActivePermissions(null);
          setResolvedAccessPropertyId(activeProperty === "all" ? "all" : null);
          setLoadingAccess(false);
        }
        return;
      }

      // Never expose the previous property's permissions while the new
      // property's access record is being resolved.
      setLoadingAccess(true);
      setActivePropertyDoc(null);
      setActivePermissions(null);
      setResolvedAccessPropertyId(null);
      try {
        const pSnap = await getPropertyDocument(db, activeProperty);
        if (cancelled) return;

        const pData = pSnap.exists() ? pSnap.data() : null;
        setActivePropertyDoc(pData ? { id: activeProperty, ...pData } : null);

        const ownerLower = String(pData?.owneremail || "").toLowerCase();
        const managerLower = String(pData?.manageremail || "").toLowerCase();
        const isOwner = ownerLower === currentUserEmailLower;
        const isManager = managerLower === currentUserEmailLower;

        if (currentUserIsGlobalAdmin) {
          setActivePermissions({
            propertyId: activeProperty,
            isSuperUser: true,
            isOwner: true,
            isManager: true,
            permissions: FULL_WRITE,
          });
          setResolvedAccessPropertyId(activeProperty);
          return;
        }

        if (isOwner || isManager) {
          setActivePermissions({
            propertyId: activeProperty,
            isOwner,
            isManager,
            permissions: FULL_WRITE,
          });
          setResolvedAccessPropertyId(activeProperty);
          return;
        }

        const aSnap = await getPropertyAccessDocument(
          db,
          activeProperty,
          currentUser.uid
        );
        if (cancelled) return;

        if (!aSnap.exists()) {
          setActivePermissions(null);
          setResolvedAccessPropertyId(activeProperty);
          return;
        }

        const perms = aSnap.data()?.permissions || {};
        const ver = aSnap.data()?.permissionsVersion || 0;

        const norm = (v) => normalizePerm(v, ver);

        setActivePermissions({
          propertyId: activeProperty,
          isOwner: false,
          isManager: false,
          permissionsVersion: ver,
          permissions: Object.fromEntries(
            PERMISSION_KEYS.map((k) => [k, norm(perms[k])])
          ),
        });
        setResolvedAccessPropertyId(activeProperty);
      } catch (e) {
        if (cancelled) return;
        console.error("[PropertyContext] Failed loading active property access", e);
        setActivePropertyDoc(null);
        setActivePermissions(null);
        setResolvedAccessPropertyId(activeProperty);
      } finally {
        if (!cancelled) setLoadingAccess(false);
      }
    };

    loadActive();
    return () => {
      cancelled = true;
    };
  }, [currentUser, currentUserEmailLower, currentUserIsGlobalAdmin, activeProperty]);

  
// ---------------------------------------------
  // PORTFOLIO (Total Property) permission intersection (LEAST PRIVILEGE)
  // ---------------------------------------------
  const portfolioPermissions = useMemo(() => {
    if (!currentUserEmailLower || !properties.length) return null;

    if (currentUserIsGlobalAdmin) {
      return FULL_WRITE;
    }

    const result = {};
    for (const key of PERMISSION_KEYS) {
      const vals = properties.map((p) => {
        const entry = allPropertyPermissions?.[p.id];
        return entry?.permissions?.[key] || "none";
      });
      result[key] = intersectPerm(vals);
    }

    return result;
  }, [properties, currentUserEmailLower, currentUserIsGlobalAdmin, allPropertyPermissions]);


  
// ---------------------------------------------
  // Permission helper (property OR portfolio)
  // action: "read" | "write" | "readWrite"
  // ---------------------------------------------
  const hasPermission = useMemo(() => {
    return (key, action = "read") => {
      const resolvedKey = resolvePermissionKey(key);
      const effective =
        activeProperty === "all"
          ? portfolioPermissions?.[resolvedKey] || "none"
          : activePermissions?.propertyId === activeProperty
          ? activePermissions?.permissions?.[resolvedKey] || "none"
          : "none";

      if (action === "read") return permHasRead(effective);
      if (action === "write") return permHasWrite(effective);
      if (action === "readWrite") return effective === "readWrite";
      return false;
    };
  }, [activeProperty, activePermissions, portfolioPermissions]);

  const accessReady =
    activeProperty === "all"
      ? portfolioPermissions !== null &&
        resolvedPortfolioAccessKey === portfolioAccessKey
      : Boolean(
          activeProperty &&
            !loadingAccess &&
            resolvedAccessPropertyId === activeProperty
        );


  // ---------------------------------------------
  // ADD / UPDATE / DELETE PROPERTY
  // ---------------------------------------------
  const addProperty = async (data) => {
    // Permission gate: Add Property is a capability stored in the same permissions model
    if (!hasPermission("addProperty", "write")) {
      throw new Error("Permission denied: addProperty");
    }
    if (!data?.name) return null;

    const slug = data.name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

    const ownerLower = String(data.owneremail || "").toLowerCase();
    const managerLower = String(data.manageremail || "").toLowerCase();

    const payload = {
      ...data,
      propertyId: slug,
      suiteCount: data.suiteCount || 0,
      accessEmails: [ownerLower, managerLower].filter(Boolean),
    };

    await setDoc(doc(db, "properties", slug), payload);
    setProperties((prev) => [...prev, { id: slug, ...payload }]);
    return payload;
  };

  const updateProperty = async (propertyId, updates) => {
    try {
      await setDoc(doc(db, "properties", propertyId), updates, { merge: true });
      setProperties((prev) =>
        prev.map((p) => (p.id === propertyId ? { ...p, ...updates } : p))
      );
      return true;
    } catch (err) {
      console.error("Failed to update property:", err);
      return false;
    }
  };

  const deleteProperty = async (id) => {
    try {
      await deleteDoc(doc(db, "properties", id));
      setProperties((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch (err) {
      console.error("Error deleting property", err);
      return false;
    }
  };

  // ---------------------------------------------
  // CONTEXT EXPORT
  // ---------------------------------------------
  return (
    <PropertyContext.Provider
      value={{
        properties,
        activeProperty,
        setActiveProperty,
        addProperty,
        updateProperty,
        deleteProperty,
        loadingProperties,
        currentUser,
        currentUserEmailLower,
        isGlobalAdmin: currentUserIsGlobalAdmin,
        activePropertyDoc,
        activePropertyData: activePropertyDoc,
        activePermissions,
        loadingAccess,
        accessReady,
        hasPermission,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperty() {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error("useProperty must be used within a PropertyProvider");
  }
  return context;
}
